import { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { api } from '../api/client';
import { ExecutionProgress, RecordResult } from '../api/types';

const POLL_INTERVAL_MS = 3000;

export function RestoreExecution() {
  const { planId } = useParams<{ planId: string }>();
  const [progress, setProgress] = useState<ExecutionProgress | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isStarting, setIsStarting] = useState(false);
  const pollRef = useRef<number | null>(null);

  function stopPolling() {
    if (pollRef.current !== null) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }

  function startPolling() {
    stopPolling();
    pollRef.current = window.setInterval(async () => {
      if (!planId) return;
      try {
        const data = await api.get<ExecutionProgress>(`/restore-runs/${planId}`);
        setProgress(data);
        if (data.status !== 'RUNNING') {
          stopPolling();
        }
      } catch {
        // Continue polling on transient errors
      }
    }, POLL_INTERVAL_MS);
  }

  useEffect(() => {
    if (!planId) return;
    let cancelled = false;

    async function fetchExecution() {
      try {
        const data = await api.get<ExecutionProgress>(`/restore-runs/${planId}`);
        if (!cancelled) {
          setProgress(data);
          if (data.status === 'RUNNING') {
            startPolling();
          }
        }
      } catch (err) {
        if (!cancelled) {
          // 404 means execution hasn't started yet
          if (err instanceof Error && err.message.includes('404')) {
            setProgress(null);
          } else {
            setError(err instanceof Error ? err.message : 'Failed to load execution status');
          }
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    }

    fetchExecution();
    return () => {
      cancelled = true;
      stopPolling();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [planId]);

  async function handleStartExecution() {
    if (!planId) return;
    setIsStarting(true);
    setError(null);

    try {
      const data = await api.post<ExecutionProgress>(`/restore-plans/${planId}/execute`);
      setProgress(data);
      if (data.status === 'RUNNING') {
        startPolling();
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to start execution');
    } finally {
      setIsStarting(false);
    }
  }

  function getProgressPercent(): number {
    if (!progress || progress.totalRecords === 0) return 0;
    return Math.round((progress.processedRecords / progress.totalRecords) * 100);
  }

  function statusColor(status: RecordResult['status']): string {
    switch (status) {
      case 'SUCCESS': return 'var(--color-success)';
      case 'FAILURE': return 'var(--color-error)';
      case 'SKIPPED': return 'var(--color-text-muted)';
      default: return 'inherit';
    }
  }

  if (isLoading) {
    return (
      <div role="status" aria-live="polite">
        <h2>Restore Execution</h2>
        <p>Loading execution status…</p>
      </div>
    );
  }

  return (
    <div>
      <h2>Restore Execution</h2>
      <p style={{ color: 'var(--color-text-muted)', fontSize: '0.875rem' }}>
        Plan: <code>{planId}</code>
      </p>

      {error && (
        <div className="card" role="alert" style={{ borderLeft: '4px solid var(--color-error)', marginBottom: '1rem' }}>
          <p style={{ color: 'var(--color-error)', margin: 0 }}>{error}</p>
        </div>
      )}

      {/* Not started yet */}
      {!progress && (
        <div className="card" style={{ textAlign: 'center', padding: '2rem' }}>
          <p style={{ marginBottom: '1rem' }}>This restore plan is approved and ready for execution.</p>
          <button
            onClick={handleStartExecution}
            disabled={isStarting}
            className="success"
            style={{ fontSize: '1rem', padding: '0.75rem 2rem' }}
          >
            {isStarting ? 'Starting…' : '▶ Start Execution'}
          </button>
        </div>
      )}

      {/* Execution Progress */}
      {progress && (
        <>
          {/* Status Header */}
          <div className="card">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <div>
                <span
                  className={`badge ${
                    progress.status === 'COMPLETED' ? 'badge-success' :
                    progress.status === 'FAILED' ? 'badge-error' :
                    progress.status === 'RUNNING' ? 'badge-info' : 'badge-warning'
                  }`}
                  style={{ fontSize: '0.85rem' }}
                >
                  {progress.status}
                </span>
                {progress.status === 'RUNNING' && (
                  <span style={{ marginLeft: '0.75rem', color: 'var(--color-text-muted)', fontSize: '0.8rem' }}>
                    Polling every {POLL_INTERVAL_MS / 1000}s…
                  </span>
                )}
              </div>
              {progress.startTime && (
                <span style={{ fontSize: '0.8rem', color: 'var(--color-text-muted)' }}>
                  Started: {new Date(progress.startTime).toLocaleString()}
                </span>
              )}
            </div>

            {/* Progress Bar */}
            <div
              role="progressbar"
              aria-valuenow={getProgressPercent()}
              aria-valuemin={0}
              aria-valuemax={100}
              aria-label="Restore progress"
              style={{
                background: 'var(--color-bg)',
                borderRadius: 'var(--radius)',
                overflow: 'hidden',
                height: '24px',
                marginBottom: '0.75rem',
              }}
            >
              <div
                style={{
                  width: `${getProgressPercent()}%`,
                  height: '100%',
                  background: progress.status === 'FAILED' ? 'var(--color-error)' : 'var(--color-primary)',
                  transition: 'width 0.3s ease',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#fff',
                  fontSize: '0.75rem',
                  fontWeight: 600,
                }}
              >
                {getProgressPercent()}%
              </div>
            </div>

            {/* Stats */}
            <div className="stat-grid">
              <div className="stat-card">
                <div className="label">Processed</div>
                <div className="value">{progress.processedRecords} / {progress.totalRecords}</div>
              </div>
              <div className="stat-card">
                <div className="label">Successful</div>
                <div className="value" style={{ color: 'var(--color-success)' }}>{progress.successCount}</div>
              </div>
              <div className="stat-card">
                <div className="label">Failed</div>
                <div className="value" style={{ color: progress.failureCount > 0 ? 'var(--color-error)' : 'inherit' }}>
                  {progress.failureCount}
                </div>
              </div>
            </div>

            {/* End Time */}
            {progress.endTime && (
              <p style={{ fontSize: '0.8rem', color: 'var(--color-text-muted)', margin: 0 }}>
                Completed: {new Date(progress.endTime).toLocaleString()}
              </p>
            )}
          </div>

          {/* Record Results */}
          {progress.recordResults.length > 0 && (
            <section aria-labelledby="results-heading" style={{ marginTop: '1.5rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
                <h3 id="results-heading" style={{ margin: 0 }}>Record Results</h3>
                {progress.auditManifestUrl && (
                  <a
                    href={progress.auditManifestUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Download audit manifest"
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '0.25rem',
                      padding: '0.4rem 0.75rem',
                      background: 'var(--color-surface)',
                      border: '1px solid var(--color-border)',
                      borderRadius: 'var(--radius)',
                      fontSize: '0.8rem',
                      textDecoration: 'none',
                    }}
                  >
                    📄 Download Audit Manifest
                  </a>
                )}
              </div>

              <div style={{ maxHeight: '500px', overflow: 'auto' }}>
                <table aria-label="Per-record execution results">
                  <thead>
                    <tr>
                      <th scope="col">Status</th>
                      <th scope="col">Object Type</th>
                      <th scope="col">Name</th>
                      <th scope="col">Original ID</th>
                      <th scope="col">New ID</th>
                      <th scope="col">Error</th>
                    </tr>
                  </thead>
                  <tbody>
                    {progress.recordResults.map((result) => (
                      <tr key={result.recordId}>
                        <td>
                          <span
                            style={{
                              fontWeight: 600,
                              color: statusColor(result.status),
                              fontSize: '0.8rem',
                            }}
                          >
                            {result.status}
                          </span>
                        </td>
                        <td><span className="badge badge-info">{result.objectType}</span></td>
                        <td>{result.name}</td>
                        <td><code style={{ fontSize: '0.75rem' }}>{result.recordId}</code></td>
                        <td>
                          {result.newRecordId ? (
                            <code style={{ fontSize: '0.75rem' }}>{result.newRecordId}</code>
                          ) : (
                            <span style={{ color: 'var(--color-text-muted)' }}>—</span>
                          )}
                        </td>
                        <td>
                          {result.errorMessage ? (
                            <span style={{ color: 'var(--color-error)', fontSize: '0.8rem' }}>
                              {result.errorMessage}
                            </span>
                          ) : (
                            <span style={{ color: 'var(--color-text-muted)' }}>—</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          )}
        </>
      )}
    </div>
  );
}
